
import * as React from 'react';
import { styled } from 'linaria/react';
import { css } from 'linaria';
import ImageGallery from 'react-image-gallery';
import 'react-image-gallery/styles/css/image-gallery.css';
import { constructGallery } from '@jetshop/ui/Gallery/constructGallery';
import Image from '@jetshop/ui/Image/Image';
import ChannelContext from '@jetshop/core/components/ChannelContext';
import { useShopConfig } from '@jetshop/core/hooks/useShopConfig';
import videoPlaceholder from '../../../img/video-placeholder.png'
import { ReactComponent as PlayButton } from '../../../svg/fa/play-circle.svg';

const wrapperStyles = css`
  position: relative;
  img {
    max-height: 100vh;
  }
  .video-wrapper {
    aspect-ratio: 1/1;
  }
  .image-gallery {
    z-index: 140;
  }
  .image-gallery-image {
    text-align: center;
  }
  .image-gallery-thumbnail-inner {
    font-size: 10px;
    &.video-thumbnail {
      [data-flight-image-container]{
        opacity: .6;
      }
      svg{
        position: absolute;
        top: 50%;
        left: 50%;
        transform: translate(-50%, -50%);
        width: 50%;
        height: 50%;
      }
    }
  }
  .image-gallery-right-nav,
  .image-gallery-left-nav,
  .image-gallery-fullscreen-button {
    z-index: 2;
    font-size: 2rem;
    display: flex;
    justify-content: center;
    align-items: center;
    padding: 0;
    height: 2.5rem;
    width: 2.5rem;
    border-radius: 50%;
    background: rgba(255, 255, 255, 0.4);
    transition: background 0.3s ease-in-out;
    margin: 0 0.5rem;
    &:hover {
      background: rgba(255, 255, 255, 0.8);
    }
    &:before {
      color: black;
      text-shadow: none;
    }
  }
  .image-gallery-fullscreen-button {
    margin-bottom: 0.5rem;
  }
  .image-gallery-fullscreen-button:before {
    text-shadow: none;
    font-size: 0.7em;
    padding: 0;
  }
  .image-gallery-thumbnail.active {
    border-width: 1px;
  }
  .image-gallery-thumbnails-wrapper.left .image-gallery-thumbnails,
  .image-gallery-thumbnails-wrapper.right .image-gallery-thumbnails {
    display: flex;
    flex-direction: column;
    justify-content: center;
  }
  .fullscreen {
    .image-gallery-fullscreen-button {
      margin: 2rem;
    }
    .image-gallery-right-nav,
    .image-gallery-left-nav {
      margin: 0 2rem;
    }
    img {
      width: auto;
      max-width: 100%;
    }
    .image-gallery-thumbnails-wrapper {
      display: none;
    }
  }
  .image-gallery-slide {
    background-color: var(--slide-bg);
  }
`;

function getYoutubeId(youtube) {
  if (!youtube?.value) {
    return null;
  }

  const youtubeLink = youtube.value.match(
    /(?:https?:\/\/|www\.|m\.|^)youtu(?:be\.com\/watch\?(?:.*?&(?:amp;)?)?v=|\.be\/)([\w‌​\-]+)(?:&(?:amp;)?[\w\?=]*)?/
  );
  return youtubeLink[1];
}

function getYoutubeEmbedLink(youtube) {
  if (!youtube?.value) {
    return null;
  }

  const youtubeLink = getYoutubeId(youtube);
  return 'https://www.youtube.com/embed/' + youtubeLink;
}

const Overlay = styled('div')`
  position: absolute;
  left: 0;
  top: 0;
  right: 0;
  bottom: 0;
  pointer-events: none;
`;
const Gallery = ({youtube, sizes = [1, 1, 1, 1 / 2, 1 / 2], fullScreenSizes = [1, 1, 1, 1], thumbnailSizes = ['5rem'], aspect = '1:1', thumbnailPosition = 'bottom', backgroundColor = 'white', thumbnailQuality, quality, images, galleryRef, eagerlyLoad = [0], ...rest}) => {
  const { theme: { breakpoints } } = useShopConfig();
  const { selectedChannel } = React.useContext(ChannelContext);
  const baseUrl = selectedChannel?.url ? String(selectedChannel.url) : null;
  const [isFullscreen, setFullscreen] = React.useState(false);
  const galleryImages = React.useMemo(() => constructGallery(images, fullScreenSizes, aspect, baseUrl, breakpoints), [aspect, baseUrl, breakpoints, fullScreenSizes, images]);
  if(youtube?.value) {
    galleryImages.push({
      original: getYoutubeEmbedLink(youtube),
      thumbnail: `https://i3.ytimg.com/vi/${getYoutubeId(youtube)}/hqdefault.jpg`,
      thumbnailTitle: 'Video',
      type: 'video'
    });
  }
  return (
    <div className={wrapperStyles} style={{ ['--slide-bg']: backgroundColor }}>
      <ImageGallery
        ref={r => (galleryRef ? (galleryRef.current = r) : null)}
        thumbnailPosition={thumbnailPosition}
        items={galleryImages}
        autoPlay={false}
        showPlayButton={false}
        showThumbnails={galleryImages.length > 1}
        useBrowserFullscreen={false}
        preventDefaultTouchmoveEvent={isFullscreen}
        onScreenChange={el => setFullscreen(!!el)}
        renderItem={item => {
          if(item.type === 'video') {
            return <div className='video-wrapper'>
              <iframe src={item.original} width="100%" height="100%" frameBorder="0" allow="accelerometer; autoplay; encrypted-media; gyroscope; picture-in-picture" allowFullScreen></iframe>
            </div>
          }
          return (
            <div className="image-gallery-image">
              {isFullscreen ? (
                <img
                  src={item.original}
                  alt={item.originalAlt}
                  srcSet={item.srcSet}
                  sizes={item.sizes}
                  title={item.originalTitle}
                />
              ) : (
                <>
                  <Image
                    src={item.original}
                    alt={item.originalAlt}
                    sizes={sizes}
                    title={item.originalTitle}
                    aspect={aspect}
                    quality={quality}
                    modifiedDate={item.modifiedDate}
                    critical={eagerlyLoad.includes(item.index)}
                  />
                  {rest.children && <Overlay>{rest.children}</Overlay>}
                </>
              )}
            </div>
          );}}
        renderThumbInner={item => {
          if(item.type === 'video') {
            return <div className="image-gallery-thumbnail-inner video-thumbnail">
                          <Image
              src={item.thumbnail}
              alt={item.thumbnailAlt}
              title={item.thumbnailTitle}
              sizes={thumbnailSizes}
              aspect={aspect}
              modifiedDate={item.modifiedDate}
              quality={thumbnailQuality}
            />
              <PlayButton/>
            </div>
          }
          
          return(
          <div className="image-gallery-thumbnail-inner">
            <Image
              src={item.thumbnail}
              alt={item.thumbnailAlt}
              title={item.thumbnailTitle}
              sizes={thumbnailSizes}
              aspect={aspect}
              modifiedDate={item.modifiedDate}
              quality={thumbnailQuality}
            />
            {item.thumbnailLabel && (
              <div className="image-gallery-thumbnail-label">
                {item.thumbnailLabel}
              </div>
            )}
          </div>
        )}}
        {...rest}
      />
    </div>
  );
};
export default Gallery;
//# sourceMappingURL=Gallery.js.map
